import type { ChatMessage } from './chat';
import { PERSONAS, buildCrossRefSection, type PersonaId } from './personas';

export interface PersonaMemory {
  summary: string;
  recent: ChatMessage[];
  lastVisit: number;
  totalTurns: number;
}

export interface SharedEvent {
  who: PersonaId;
  user: string;
  ts: number;
  tags: string[];
}

const SUMMARY_TURN_THRESHOLD = 5;
const SHARED_KEY = 'wsky:shared';
const SHARED_MAX = 12;
const SHARED_INJECT_LIMIT = 3;

const SHARED_KEYWORD_TAGS: Array<{ tag: string; keys: string[] }> = [
  { tag: '名字', keys: ['名字', '我叫', '我是'] },
  { tag: '想家', keys: ['想家', '孤独', '一个人', '想念家', '没有家'] },
  { tag: '心事', keys: ['喜欢', '爱', '在乎', '难过', '伤心', '哭'] },
  { tag: '秘密', keys: ['秘密', '嘘', '别说', '别告诉', '保密'] },
  { tag: '端午', keys: ['端午', '粽子', '龙舟', '节日', '过节'] }
];

function extractSharedTags(userText: string): string[] {
  const tags: string[] = [];
  for (const { tag, keys } of SHARED_KEYWORD_TAGS) {
    if (keys.some(k => userText.includes(k))) tags.push(tag);
  }
  return tags;
}

export function loadSharedEvents(): SharedEvent[] {
  try {
    const raw = localStorage.getItem(SHARED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as SharedEvent[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// Single-tab assumption: read-modify-write is not atomic in localStorage.
// If user opens whisper-sky in two tabs and writes simultaneously, the later
// write wins (last-write-wins, no merge). H5 deployment makes this rare.
function saveSharedEvents(events: SharedEvent[]): void {
  try {
    localStorage.setItem(SHARED_KEY, JSON.stringify(events.slice(-SHARED_MAX)));
  } catch {
    // localStorage quota — drop silently
  }
}

export function recordSharedEvent(personaId: PersonaId, userText: string): void {
  const tags = extractSharedTags(userText);
  if (!tags.length) return;
  const events = loadSharedEvents();
  const trimmedUser = userText.replace(/\s+/g, ' ').slice(0, 50);
  events.push({ who: personaId, user: trimmedUser, ts: Date.now(), tags });
  saveSharedEvents(events);
}

function buildSharedRumorSection(currentPersonaId: PersonaId): string {
  const all = loadSharedEvents().filter(e => e.who !== currentPersonaId);
  const seen = new Set<string>();
  const deduped: SharedEvent[] = [];
  for (let i = all.length - 1; i >= 0 && deduped.length < SHARED_INJECT_LIMIT; i--) {
    const e = all[i];
    const key = `${e.who}|${e.tags.slice().sort().join(',')}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(e);
  }
  if (!deduped.length) return '';
  const lines = deduped.reverse().map(e => {
    const heard = PERSONAS[e.who].shortName;
    return `- ${heard} 那边玩家说过："${e.user}"（涉及：${e.tags.join(' / ')}）`;
  });
  return [
    '\n[你听说过玩家在别处说的话，可以在合适时机不点破地呼应——不要照抄原文，不要每次都全部提到]',
    ...lines
  ].join('\n');
}

function key(personaId: PersonaId): string {
  return `wsky:memory:${personaId}`;
}

export function loadMemory(personaId: PersonaId): PersonaMemory {
  try {
    const raw = localStorage.getItem(key(personaId));
    if (!raw) return emptyMemory();
    const parsed = JSON.parse(raw) as PersonaMemory;
    if (!Array.isArray(parsed.recent)) parsed.recent = [];
    if (typeof parsed.summary !== 'string') parsed.summary = '';
    if (typeof parsed.totalTurns !== 'number') parsed.totalTurns = 0;
    if (typeof parsed.lastVisit !== 'number') parsed.lastVisit = 0;
    return parsed;
  } catch {
    return emptyMemory();
  }
}

function emptyMemory(): PersonaMemory {
  return { summary: '', recent: [], lastVisit: 0, totalTurns: 0 };
}

export function saveMemory(personaId: PersonaId, mem: PersonaMemory): void {
  try {
    localStorage.setItem(key(personaId), JSON.stringify(mem));
  } catch (err) {
    console.warn('[memory] save failed', err);
  }
}

export function clearMemory(personaId: PersonaId): void {
  try {
    localStorage.removeItem(key(personaId));
  } catch {
    // ignore
  }
}

export function appendTurn(mem: PersonaMemory, user: string, assistant: string): PersonaMemory {
  const updated: PersonaMemory = {
    summary: mem.summary,
    recent: [
      ...mem.recent,
      { role: 'user', content: user },
      { role: 'assistant', content: assistant }
    ],
    lastVisit: Date.now(),
    totalTurns: mem.totalTurns + 1
  };
  return updated;
}

export function shouldCompress(mem: PersonaMemory): boolean {
  return mem.recent.length >= SUMMARY_TURN_THRESHOLD * 2;
}

export function buildSystemPromptWithMemory(
  basePrompt: string,
  mem: PersonaMemory,
  personaId?: PersonaId
): string {
  const ctx: string[] = [basePrompt];
  if (personaId) {
    const refSection = buildCrossRefSection(PERSONAS[personaId]);
    if (refSection) ctx.push(refSection);
  }
  if (mem.summary) {
    ctx.push(`\n\n[与玩家既往交往的摘要，请自然引用，不要复述编号]\n${mem.summary}`);
  }
  if (personaId) {
    const rumorSection = buildSharedRumorSection(personaId);
    if (rumorSection) ctx.push(rumorSection);
  }
  if (mem.totalTurns > 0) {
    ctx.push(`\n[这是你们的第 ${mem.totalTurns + 1} 次通信。]`);
  }
  return ctx.join('');
}

export function compressLocally(mem: PersonaMemory): PersonaMemory {
  if (!shouldCompress(mem)) return mem;
  const lines: string[] = [];
  for (let i = 0; i < mem.recent.length; i += 2) {
    const u = mem.recent[i]?.content ?? '';
    const a = mem.recent[i + 1]?.content ?? '';
    const uSnippet = u.replace(/\s+/g, ' ').slice(0, 40);
    const aSnippet = a.replace(/\s+/g, ' ').slice(0, 40);
    lines.push(`- 玩家说"${uSnippet}"，你回了"${aSnippet}"。`);
  }
  const merged = [mem.summary, '近期对话片段：', ...lines].filter(Boolean).join('\n');
  return {
    ...mem,
    summary: trimSummary(merged),
    recent: mem.recent.slice(-2)
  };
}

function trimSummary(s: string): string {
  const MAX = 800;
  if (s.length <= MAX) return s;
  return s.slice(s.length - MAX);
}

export async function compressViaLLM(
  mem: PersonaMemory,
  summarize: (history: ChatMessage[], oldSummary: string) => Promise<string>
): Promise<PersonaMemory> {
  if (!shouldCompress(mem)) return mem;
  try {
    const summary = await summarize(mem.recent, mem.summary);
    return {
      ...mem,
      summary: trimSummary(summary),
      recent: mem.recent.slice(-2)
    };
  } catch (err) {
    console.warn('[memory] llm compress failed, using local', err);
    return compressLocally(mem);
  }
}
